import { Button } from '@optools/components/ui/button';
import { cn } from '@optools/components/ui/utils';
import { useAccessCheck } from '@optools/hooks/useAccessCheck';
import { selectApplicationConfig } from '@optools/store/configApiSlice';
import { useAppSelector } from '@optools/store/hooks';
import {
  ClipboardCheck,
  FileVideo2,
  Film,
  House,
  ListChecks,
  Logs,
  Megaphone,
  PanelLeftClose,
  PanelLeftOpen,
  RedoDot,
  Replace,
  Route,
  SatelliteDish,
  Tv,
  UserSearch,
} from 'lucide-react';

import { AccountMenu } from './account-menu';
import { SettingsMenu } from './settings-menu';
import { SidebarLink } from './sidebar-link';

export interface SidebarProps {
  onToggleOpen: () => void;
  open: boolean;
}

export function Sidebar({ onToggleOpen, open }: SidebarProps) {
  const {
    showAudit,
    showBtcTools,
    showEsni,
    showIpOverride,
    showLogger,
    showMediaValidation,
    showMilestones,
    showOverrides,
    showPartnerStreamDelivery,
    showQaTools,
    showUserLookup,
  } = useAccessCheck();

  const { submitFeedbackLink } = useAppSelector(selectApplicationConfig);

  return (
    <aside
      className={cn(
        'inset-y ease left-0 z-20 flex h-full flex-col overflow-x-hidden overflow-y-auto border-r transition-[width] duration-300',
        open ? 'w-64' : 'w-14',
      )}>
      <div className="relative -left-px flex items-center justify-end border-b p-2">
        <h1
          className={cn(
            'ease absolute left-5 text-xs font-bold whitespace-nowrap transition-opacity duration-300',
            open ? 'opacity-100 delay-100' : 'opacity-0',
          )}>
          Streaming Operator Platform
        </h1>
        <Button
          aria-label={`${open ? 'Close' : 'Open'} sidebar`}
          className="relative z-10"
          size="icon-lg"
          title={`${open ? 'Close' : 'Open'} sidebar`}
          variant="ghost"
          onClick={onToggleOpen}>
          {open ? <PanelLeftClose className="size-5" /> : <PanelLeftOpen className="size-5" />}
        </Button>
      </div>
      <nav className="flex flex-col gap-2 overflow-x-hidden p-2">
        <SidebarLink activePath="/" icon={House} name="Home" sidebarOpen={open} to="/" />

        {showBtcTools && (
          <SidebarLink
            activePath="/btc-tools"
            icon={SatelliteDish}
            name="BTC Tools"
            sidebarOpen={open}
            to="/btc-tools/schedule"
          />
        )}

        {showOverrides && (
          <SidebarLink
            activePath="/overrides"
            icon={RedoDot}
            name="Overrides"
            sidebarOpen={open}
            to="/overrides/session"
          />
        )}
        {showIpOverride && (
          <SidebarLink
            activePath="/ip-override"
            icon={Replace}
            name="Blackout Override"
            sidebarOpen={open}
            to="/ip-override/user-lookup"
          />
        )}
        {showLogger && <SidebarLink icon={Tv} name="Logger" sidebarOpen={open} to="/logger" />}
        {showMediaValidation && (
          <SidebarLink
            activePath="/media-validation"
            icon={ClipboardCheck}
            name="Media Validation"
            sidebarOpen={open}
            to="/media-validation/litmus"
          />
        )}
        {showMilestones && (
          <SidebarLink icon={Film} name="Milestones" sidebarOpen={open} to="/milestones" />
        )}
        {showAudit && (
          <SidebarLink
            activePath="/audit"
            icon={Logs}
            name="Audit & Logging"
            sidebarOpen={open}
            to="/audit/audits"
          />
        )}
        {showPartnerStreamDelivery && (
          <SidebarLink
            activePath="/partner"
            icon={Route}
            name="Partner Delivery"
            sidebarOpen={open}
            to="/partner/mapping"
          />
        )}
        {showEsni && (
          <SidebarLink
            activePath="/esni"
            icon={FileVideo2}
            name="ESNI Management"
            sidebarOpen={open}
            to="/esni/media"
          />
        )}
        {showUserLookup && (
          <SidebarLink icon={UserSearch} name="User Lookup" sidebarOpen={open} to="/user-lookup" />
        )}
        {showQaTools && (
          <SidebarLink
            icon={ListChecks}
            name="QA Tools"
            sidebarOpen={open}
            to="/qa-tools/dev-configs"
          />
        )}
      </nav>

      <nav className="mt-auto grid gap-1 p-2">
        <SidebarLink
          external={true}
          icon={Megaphone}
          name="Submit Feedback"
          sidebarOpen={open}
          to={submitFeedbackLink}
        />
        <SettingsMenu sidebarOpen={open} />
        <AccountMenu sidebarOpen={open} />
      </nav>
    </aside>
  );
}
